import {
  Component,
  inject,
  input,
  output,
  signal,
  computed,
  booleanAttribute,
  type InputSignal,
  type OutputEmitterRef,
  type WritableSignal,
  type Signal,
  type InputSignalWithTransform,
  ElementRef
} from '@angular/core';
import { SelectionChangeEvent } from '../../domain/selectable.domain';
import { IdService } from '../../services/id.service';

import type { SelectableItem } from '../../domain/selectable.domain';

@Component({
  selector: 'td-wb-side-sheet-selector-item',
  templateUrl: './side-sheet-selector-item.component.html',
  styleUrls: ['side-sheet-selector-item.component.scss'],
  host: {
    '[class.td-wb-side-sheet-selector-item--active]': 'active()',
    '[class.td-wb-side-sheet-selector-item--selected]': 'selected()',
    '[class.td-wb-side-sheet-selector-item--first]': 'first()',
    '[class.td-wb-side-sheet-selector-item--last]': 'last()',
    '[attr.id]': 'id',
    '[attr.role]': '"option"',
    '[attr.tabindex]': 'tabIndex()',
    '[attr.aria-disabled]': 'disabled.toString()',
    '[attr.aria-selected]': 'ariaSelected()',
    '(keydown.enter)': 'onKeyDown()',
    '(keydown.space)': 'onKeyDown()',
    '(click)': 'onClick()'
  }
})

export class SideSheetSelectorItemComponent<T> implements SelectableItem<T> {
  private static readonly ID_PREFIX: string = 'td-wb-side-sheet-selector-option';

  readonly value: InputSignal<T | undefined> = input<T | undefined>();

  readonly disabledSignal: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, { alias: 'disabled', transform: booleanAttribute });

  get disabled(): boolean {
    return this.disabledSignal();
  }

  readonly onSelectionChange: OutputEmitterRef<SelectionChangeEvent<SelectableItem<T>>>
    = output<SelectionChangeEvent<SelectableItem<T>>>();

  readonly active: WritableSignal<boolean> = signal(false);
  readonly selected: WritableSignal<boolean> = signal(false);
  readonly first: WritableSignal<boolean> = signal(false);
  readonly last: WritableSignal<boolean> = signal(false);

  private readonly idService = inject(IdService);
  readonly id: string = this.idService.generateNamespaceSequentialId(SideSheetSelectorItemComponent.ID_PREFIX);

  readonly elementRef = inject(ElementRef);

  readonly tabIndex: Signal<string> = computed((): string => this.disabled ? '-1' : '0');

  readonly ariaSelected: Signal<string | undefined> = computed((): string | undefined => {
    return !this.active() ? undefined : 'true';
  });

  onKeyDown(): boolean {
    this.select(true);
    return false;
  }

  onClick(): void {
    this.select(true);
  }

  /**
   * Highlightable.setActiveStyles
   */
  setActiveStyles(): void {
    if (!this.active()) {
      this.active.set(true);
    }
  }

  /**
   * Highlightable.setInactiveStyles
   */
  setInactiveStyles(): void {
    if (this.active()) {
      this.active.set(false);
    }
  }

  /**
   * SelectableItem.getLabel
   */
  getLabel(): string {
    return this.elementRef.nativeElement.textContent?.trim() ?? '';
  }

  /**
   * SelectableItem.select
   */
  select(fromView: boolean = false): void {
    if (!this.selected() || (fromView && !this.disabled)) {
      this.selected.set(true);
      this.emitSelectionChangeEvent(fromView);
    }
  }

  /**
   * SelectableItem.deselect
   */
  deselect(emitEvent: boolean = true): void {
    if (this.selected()) {
      this.selected.set(false);
      if (emitEvent) {
        this.emitSelectionChangeEvent();
      }
    }
  }

  /**
   * SelectableItem.getValue
   */
  getValue(): T {
    return this.value();
  }

  /**
   * SelectableItem.isSelected
   */
  isSelected(): boolean {
    return this.selected();
  }

  isActive(): boolean {
    return this.active();
  }

  focus(): void {
    this.elementRef.nativeElement.focus();
  }

  private emitSelectionChangeEvent(fromView: boolean = false): void {
    this.onSelectionChange.emit(new SelectionChangeEvent(this, fromView));
  }

}
